import React, { useState, useEffect } from "react";
import {
  Container,
  Paper,
  Box,
  TextField,
  Button,
  Snackbar,
  Alert,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  LinearProgress,
} from "@mui/material";
import { createBlog, getAllBlogCategoriesAdmin } from "../../services/api";

export default function AddBlog() {
  const [title, setTitle] = useState("");
  const [categoryId, setCategoryId] = useState("");
  const [authorName, setAuthorName] = useState("");
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [content, setContent] = useState("");
  const [isPublished, setIsPublished] = useState(true);

  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [snack, setSnack] = useState({
    open: false,
    severity: "success",
    message: "",
  });

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const res = await getAllBlogCategoriesAdmin();
        setCategories(res?.data?.data || []);
      } catch (err) {
        console.error("Failed to fetch categories", err);
        setSnack({
          open: true,
          severity: "error",
          message: "Failed to load categories",
        });
      }
    };
    fetchCategories();
  }, []);

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setImageFile(file);
      setImagePreview(URL.createObjectURL(file));
    }
  };

  const handleSubmit = async () => {
    if (!title.trim() || !categoryId || !content.trim()) {
      setSnack({
        open: true,
        severity: "error",
        message: "Title, category, and content are required.",
      });
      return;
    }

    const formData = new FormData();
    formData.append("title", title.trim());
    formData.append("categoryId", categoryId);
    formData.append("authorName", authorName.trim());
    formData.append("content", content);
    formData.append("isPublished", isPublished);
    if (imageFile) formData.append("image", imageFile);

    setLoading(true);
    try {
      await createBlog(formData);
      setSnack({
        open: true,
        severity: "success",
        message: "Blog created successfully!",
      });

      // Reset form
      setTitle("");
      setCategoryId("");
      setAuthorName("");
      setContent("");
      setImageFile(null);
      setImagePreview(null);
      setIsPublished(true);
    } catch (err) {
      console.error("Create blog error", err);
      const msg =
        err?.response?.data?.error ||
        err?.response?.data?.message ||
        err?.message ||
        "Failed to create blog";
      setSnack({ open: true, severity: "error", message: msg });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ pt: { xs: 8, md: 10 }, pb: 6 }}>
      <Container maxWidth="md">
        <Paper sx={{ p: 3, boxShadow: "0 4px 20px rgba(0,0,0,0.3)" }}>
          <Typography variant="h6" mb={2}>
            Add Blog
          </Typography>

          <Box display="flex" flexDirection="column" gap={2}>
            <TextField
              label="Blog Title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              fullWidth
              disabled={loading}
            />

            <FormControl fullWidth disabled={loading}>
              <InputLabel>Category</InputLabel>
              <Select
                value={categoryId}
                label="Category"
                onChange={(e) => setCategoryId(e.target.value)}
              >
                {categories.map((cat) => (
                  <MenuItem key={cat.id} value={cat.id}>
                    {cat.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              label="Author Name"
              value={authorName}
              onChange={(e) => setAuthorName(e.target.value)}
              fullWidth
              disabled={loading}
            />

            <Box>
              <Typography variant="subtitle2" mb={1}>
                Upload Blog Image
              </Typography>
              <input
                accept="image/*"
                type="file"
                onChange={handleImageChange}
                disabled={loading}
              />
              {imagePreview && (
                <Box mt={2}>
                  <img
                    src={imagePreview}
                    alt="Preview"
                    style={{
                      width: "100%",
                      maxHeight: 250,
                      objectFit: "cover",
                      borderRadius: 8,
                    }}
                  />
                </Box>
              )}
            </Box>

            <TextField
              label="Blog Content"
              multiline
              minRows={6}
              value={content}
              onChange={(e) => setContent(e.target.value)}
              fullWidth
              disabled={loading}
            />

            <FormControlLabel
              control={
                <Switch
                  checked={isPublished}
                  onChange={(e) => setIsPublished(e.target.checked)}
                />
              }
              label="Publish Now"
            />

            {loading && <LinearProgress />}

            <Button
              variant="contained"
              color="primary"
              onClick={handleSubmit}
              disabled={loading}
            >
              Add Blog
            </Button>
          </Box>
        </Paper>

        <Snackbar
          open={snack.open}
          autoHideDuration={3000}
          onClose={() => setSnack((s) => ({ ...s, open: false }))}
        >
          <Alert
            onClose={() => setSnack((s) => ({ ...s, open: false }))}
            severity={snack.severity}
            sx={{ width: "100%" }}
          >
            {snack.message}
          </Alert>
        </Snackbar>
      </Container>
    </Box>
  );
}
